import { z } from 'zod';
import { AttributeEnum, SkillEnum } from "../entity/types.js";
export const QuestTypeSchema = z.enum(['fetch', 'deliver', 'attack', 'investigate']);
// 'failed' is terminal and only ever set by a quest-failure escalation that takes the
// objective permanently out of reach (see questFailureService). It is deliberately excluded
// from every "open quests" query — a failed quest must not silently self-heal — but it is
// still surfaced to quest-giver prompts so the giver can acknowledge the loss.
export const QuestStatusSchema = z.enum(['draft', 'assigned', 'active', 'ready_to_complete', 'completed', 'failed']);
export const QuestItemSpecSchema = z.discriminatedUnion('type', [
    // References an existing quest-item ItemInstance the giver NPC holds; acquired
    // by transferring that instance to the player.
    z.object({ type: z.literal('existing_item'), instanceId: z.string() }),
    // A freshly-created quest item (its def is created with isQuestItem: true).
    z.object({ type: z.literal('item_def'), defId: z.string() }),
]);
export const QuestAcquisitionMethodSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('hidden') }),
    z.object({ type: z.literal('npc_holder'), npcId: z.string() }),
    z.object({ type: z.literal('guarded'), enemyTypeId: z.string() }),
]);
export const QuestMechanicsSchema = z.discriminatedUnion('questType', [
    z.object({
        questType: z.literal('attack'),
        enemyTypeId: z.string(),
    }),
    z.object({
        questType: z.literal('investigate'),
        // The on-map entity whose location the quest reveals on completion: a placed
        // (possibly hidden) place, or an NPC. Off-scene places are never targeted.
        targetId: z.string().optional(),
        targetKind: z.enum(['place', 'npc']).optional(),
    }),
    z.object({
        questType: z.literal('deliver'),
        itemSpec: QuestItemSpecSchema,
        recipientNpcId: z.string(),
    }),
    z.object({
        questType: z.literal('fetch'),
        itemSpec: QuestItemSpecSchema,
        acquisitionMethod: QuestAcquisitionMethodSchema,
    }),
]);
export const QuestGenerateOutputSchema = z.object({
    quest_type: QuestTypeSchema,
    outline: z.string().min(1),
});
export const QuestNarrativeOutputSchema = z.object({
    title: z.string().min(1),
    description: z.string().min(1),
    short_description: z.string().min(1),
});
export const QuestEncounterIntroOutputSchema = z.object({
    text: z.string().min(1),
    engage_response: z.string().min(1),
    leave_response: z.string().min(1),
});
export const QuestCompleteOutputSchema = z.object({
    text: z.string().min(1),
    reward_text: z.string().min(1),
    memory: z.string().optional(),
});
export const QuestExploreTypeOutputSchema = z.object({
    encounter_type: z.enum(['skill_check', 'ambush', 'discovery']),
    overview: z.string().min(1),
});
export const QuestExploreSkillOutputSchema = z.object({
    attribute: z.nativeEnum(AttributeEnum),
    skill: z.nativeEnum(SkillEnum),
});
export const QuestExploreTextOutputSchema = z.object({
    intro_text: z.string().min(1),
    engage_response: z.string().min(1),
    success_text: z.string().min(1),
    failure_text: z.string().nullable().optional().transform(v => v ?? undefined),
    success_outcome_note: z.string().min(1),
    failure_outcome_note: z.string().nullable().optional().transform(v => v ?? undefined),
});
// Establishing beat for a stumbled-discovery encounter: framing the moment the
// player comes upon an unsought site, before the exploration challenge begins.
export const DiscoveryIntroOutputSchema = z.object({
    intro_text: z.string().min(1),
    enter_response: z.string().min(1),
});
export const QuestSearchNodeOutputSchema = z.object({
    intro_text: z.string().min(1),
    engage_response: z.string().min(1),
    success_text: z.string().min(1),
    failure_text: z.string().nullable().optional().transform(v => v ?? undefined),
});
// Replacement text for a quest beat's dead-end failure node, written *after* the
// escalation's mechanics are resolved so it can name the guardian that was actually
// chosen, the rival who actually took the prize, or the hex it actually moved to.
export const QuestFailureEscalationOutputSchema = z.object({
    text: z.string().min(1),
});
export const QuestDTOSchema = z.object({
    id: z.string(),
    gameId: z.string(),
    questGiverNpcId: z.string(),
    questGiverNpcName: z.string(),
    questType: QuestTypeSchema,
    title: z.string(),
    description: z.string(),
    shortDescription: z.string(),
    mechanics: QuestMechanicsSchema,
    targetEncounterId: z.string().optional(),
    location: z.object({ x: z.number().int(), y: z.number().int() }).optional(),
    targetHex: z.object({ x: z.number().int(), y: z.number().int() }).optional(),
    questGiverHex: z.object({ x: z.number().int(), y: z.number().int() }).optional(),
    rewardLootTable: z.string(),
    rewardItemInstanceId: z.string().optional(),
    companionNpcId: z.string().optional(),
    // A player-initiated theft (no quest giver to report back to): it auto-completes the
    // moment the item is stolen rather than routing through a return-to-giver step.
    selfInitiated: z.boolean().optional(),
    status: QuestStatusSchema,
    assignedDay: z.number().int(),
    completedDay: z.number().int().optional(),
    // Why a quest ended the way it did — set when a failure escalation closes it out, and
    // shown in the journal so a failed entry says what was lost rather than just going grey.
    outcomeNote: z.string().optional(),
});
